import clsx from 'clsx'
import LocalFloorMapStage from 'components/LocalFloorMapStage/LocalFloorMapStage'
import {
  type PointerEvent,
  type RefObject,
  type WheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import style from './GausiumMapPreviewOverlay.module.css'

/**
 * Live robot pose on the GS floor map (raw pixel coordinates from vendor telemetry).
 * Origin is the BOTTOM-LEFT corner of the PNG, x grows to the right, y grows up,
 * `angleDeg` is in degrees, CCW positive, 0° = facing +x (right).
 */
export interface GausiumMapRobotMarker {
  /** Pixel x within the floor PNG (0..imageNaturalWidth). */
  x: number
  /** Pixel y within the floor PNG (0..imageNaturalHeight), measured from BOTTOM. */
  y: number
  /** Heading in degrees, CCW+, 0 = +x (right). */
  angleDeg: number
  /** Pre-resolved icon image URL (same PNG used by Mapbox `RobotOnMap` for this vendor/type). */
  iconUrl: string
  /**
   * Per-icon heading correction (deg) applied on top of telemetry heading.
   * Use when source PNG has a different "forward" direction than the default icon convention.
   */
  headingOffsetDeg?: number
  /** CSS pixel size of the robot icon on screen (defaults to 40 if omitted). */
  iconSizePx?: number
  /** Occupancy meters per cell. With spriteLengthM, the stage sizes the icon from the grid. */
  resolutionM?: number
  /** Ground length of the full map PNG. */
  spriteLengthM?: number
  /** Pose to the front edge of that canvas, meters. */
  originToFrontM?: number | null
  /** Transition duration for move/rotate animation in milliseconds. */
  transitionMs?: number
}

export interface GausiumMapTrailPoint {
  x: number
  y: number
}

export interface GausiumMapPreviewOverlayProps {
  /** When false, nothing is rendered. */
  visible: boolean
  previewUrl: string | null | undefined
  mapName?: string
  /** Ref to the map widget root (e.g. mapWrapper). Clicks outside this node close the overlay. */
  mapWidgetRootRef: RefObject<HTMLElement | null>
  onClose: () => void
  /**
   * If set, the overlay (dim + preview) only covers the map from this x offset (px) to the right edge.
   * Use when a list panel sits on the left so the preview is limited to the visible map column.
   */
  insetLeftPx?: number
  /**
   * If set, the close button is absolutely positioned from the top of the overlay (e.g. below page header controls).
   * If omitted, close stays in the top header row next to the title.
   */
  closeOffsetTopPx?: number
  /**
   * If provided, render a live robot icon on top of the floor PNG at the supplied pixel pose.
   * Hidden automatically when there is no PNG to align against.
   */
  robotMarker?: GausiumMapRobotMarker | null
  /** Optional trail polyline points in floor-PNG pixels (origin bottom-left). */
  trailPoints?: GausiumMapTrailPoint[]
  /**
   * Show a centered spinner instead of the "preview unavailable" stub while the SPA is
   * waiting for `ensureGausiumMapPreview` to resolve. Has no effect when `previewUrl` is set.
   */
  loadingPreview?: boolean
  /** Optional custom explanation when preview is not available. */
  noPreviewMessage?: string
  /** If false, overlay cannot be dismissed by dim click / outside click / Esc / close button. */
  dismissible?: boolean
  /** Keep zoom/pan when previewUrl frequently changes (e.g. live occupancy snapshots). */
  preserveViewOnSrcChange?: boolean
}

function resolveMediaSrc(url: string): string {
  const t = url.trim()
  if (t.startsWith('http://') || t.startsWith('https://')) return t
  if (typeof window !== 'undefined' && t.startsWith('/')) {
    return `${window.location.origin}${t}`
  }
  return t
}

function computeMarkerScreenPx(
  x: number,
  y: number,
  natural: { w: number; h: number },
  container: { w: number; h: number }
): { left: number; top: number } | null {
  if (!natural.w || !natural.h || !container.w || !container.h) return null
  const containerRatio = container.w / container.h
  const imageRatio = natural.w / natural.h
  let dw: number
  let dh: number
  if (containerRatio > imageRatio) {
    dh = container.h
    dw = dh * imageRatio
  } else {
    dw = container.w
    dh = dw / imageRatio
  }
  const offsetX = (container.w - dw) / 2
  const offsetY = (container.h - dh) / 2
  const scaleX = dw / natural.w
  const scaleY = dh / natural.h
  const left = offsetX + x * scaleX
  const top = offsetY + (natural.h - y) * scaleY
  return { left, top }
}

function parseScreenPoint(point: string): { left: number; top: number } | null {
  const [leftRaw, topRaw] = point.split(',')
  const left = Number(leftRaw)
  const top = Number(topRaw)
  if (!Number.isFinite(left) || !Number.isFinite(top)) return null
  return { left, top }
}

function distanceSquared(a: { left: number; top: number }, b: { left: number; top: number }): number {
  const dx = a.left - b.left
  const dy = a.top - b.top
  return dx * dx + dy * dy
}

function normalizeAngleDeg(angle: number): number {
  let a = angle % 360
  if (a > 180) a -= 360
  if (a <= -180) a += 360
  return a
}

function shortestAngleDeltaDeg(fromDeg: number, toDeg: number): number {
  return normalizeAngleDeg(toDeg - fromDeg)
}

/** Floor PNG + optional robot marker. */
const GausiumFloorImageWithRobot = ({
  src,
  alt,
  robotMarker,
  trailPoints,
  onError,
}: {
  src: string
  alt: string
  robotMarker?: GausiumMapRobotMarker | null
  trailPoints?: GausiumMapTrailPoint[]
  onError: () => void
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [container, setContainer] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [natural, setNatural] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [animatedRobotScreen, setAnimatedRobotScreen] = useState<{
    left: number
    top: number
    rotateDeg: number
  } | null>(null)
  const animatedRobotScreenRef = useRef<{
    left: number
    top: number
    rotateDeg: number
  } | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const dragRef = useRef<{ active: boolean; x: number; y: number; tx: number; ty: number }>({
    active: false,
    x: 0,
    y: 0,
    tx: 0,
    ty: 0,
  })

  // Drop stale natural dims on PNG swap so markers don't blink at old coordinates between loads.
  useEffect(() => {
    setNatural({ w: 0, h: 0 })
    setScale(1)
    setTranslate({ x: 0, y: 0 })
    setAnimatedRobotScreen(null)
    animatedRobotScreenRef.current = null
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [src])

  useEffect(() => {
    const node = wrapRef.current
    if (!node) return
    setContainer({ w: node.clientWidth, h: node.clientHeight })
    const obs = new ResizeObserver(entries => {
      const cr = entries[0]?.contentRect
      if (!cr) return
      setContainer({ w: cr.width, h: cr.height })
    })
    obs.observe(node)
    return () => obs.disconnect()
  }, [])

  const robotScreen = useMemo(() => {
    if (!robotMarker || !natural.w || !natural.h || !container.w || !container.h) return null
    const pos = computeMarkerScreenPx(robotMarker.x, robotMarker.y, natural, container)
    if (!pos) return null
    const rotateDeg = 90 - robotMarker.angleDeg + (robotMarker.headingOffsetDeg ?? 0)
    return { ...pos, rotateDeg }
    // Primitives only: parent often passes a new `robotMarker` object each poll with the same pose.
  }, [
    robotMarker?.x,
    robotMarker?.y,
    robotMarker?.angleDeg,
    robotMarker?.headingOffsetDeg,
    natural.w,
    natural.h,
    container.w,
    container.h,
  ])
  const transitionMs = Math.max(0, Math.min(8000, Number(robotMarker?.transitionMs ?? 250)))
  const transitionMsRef = useRef(transitionMs)
  transitionMsRef.current = transitionMs

  useEffect(() => {
    if (!robotScreen) {
      setAnimatedRobotScreen(null)
      animatedRobotScreenRef.current = null
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    const from = animatedRobotScreenRef.current ?? robotScreen
    const to = robotScreen
    if (animationFrameRef.current != null) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    const durationMs = transitionMsRef.current
    if (durationMs <= 0) {
      animatedRobotScreenRef.current = to
      setAnimatedRobotScreen(to)
      return
    }
    const startTime = performance.now()
    const rotateDeltaDeg = shortestAngleDeltaDeg(from.rotateDeg, to.rotateDeg)
    const animate = (now: number) => {
      const elapsed = now - startTime
      const t = Math.max(0, Math.min(1, elapsed / transitionMsRef.current))
      const next = {
        left: from.left + (to.left - from.left) * t,
        top: from.top + (to.top - from.top) * t,
        rotateDeg: from.rotateDeg + rotateDeltaDeg * t,
      }
      animatedRobotScreenRef.current = next
      setAnimatedRobotScreen(next)
      if (t < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        animationFrameRef.current = null
      }
    }
    animationFrameRef.current = requestAnimationFrame(animate)
    return () => {
      if (animationFrameRef.current != null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [robotScreen])

  const trailScreenPoints = useMemo(() => {
    if (!trailPoints?.length || !natural.w || !natural.h || !container.w || !container.h) return [] as string[]
    const mapped = trailPoints
      .map(p => computeMarkerScreenPx(p.x, p.y, natural, container))
      .filter((p): p is { left: number; top: number } => !!p)
      .map(p => `${p.left},${p.top}`)
    // Keep trail endpoint attached to the currently rendered robot marker position
    // even when backend trail order flips between [oldest..newest] and [newest..oldest].
    if (animatedRobotScreen && mapped.length > 0) {
      const first = parseScreenPoint(mapped[0])
      const last = parseScreenPoint(mapped[mapped.length - 1])
      const robotPoint = { left: animatedRobotScreen.left, top: animatedRobotScreen.top }
      if (first && last) {
        const firstDistance = distanceSquared(first, robotPoint)
        const lastDistance = distanceSquared(last, robotPoint)
        const endIndex = firstDistance <= lastDistance ? 0 : mapped.length - 1
        mapped[endIndex] = `${animatedRobotScreen.left},${animatedRobotScreen.top}`
      } else {
        mapped[mapped.length - 1] = `${animatedRobotScreen.left},${animatedRobotScreen.top}`
      }
    }
    return mapped
  }, [trailPoints, natural, container, animatedRobotScreen])

  const iconSize = robotMarker?.iconSizePx ?? 40
  const canPan = scale > 1.001

  const onWheel = useCallback(
    (e: WheelEvent<HTMLDivElement>) => {
      e.preventDefault()
      const next = Math.max(1, Math.min(4, scale + (e.deltaY < 0 ? 0.12 : -0.12)))
      setScale(next)
      if (next <= 1) {
        setTranslate({ x: 0, y: 0 })
      }
    },
    [scale]
  )

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!canPan) return
      dragRef.current = {
        active: true,
        x: e.clientX,
        y: e.clientY,
        tx: translate.x,
        ty: translate.y,
      }
      ;(e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId)
    },
    [canPan, translate.x, translate.y]
  )

  const onPointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current
    if (!d.active) return
    const dx = e.clientX - d.x
    const dy = e.clientY - d.y
    setTranslate({ x: d.tx + dx, y: d.ty + dy })
  }, [])

  const onPointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
    dragRef.current.active = false
    try {
      ;(e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId)
    } catch {
      // no-op
    }
  }, [])

  return (
    <div
      ref={wrapRef}
      className={style.imgWrap}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ cursor: canPan ? (dragRef.current.active ? 'grabbing' : 'grab') : 'default' }}
    >
      <div
        className={style.scene}
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
        }}
      >
        <img
          className={style.img}
          src={src}
          alt={alt}
          draggable={false}
          onLoad={e => {
            const t = e.currentTarget
            setNatural({ w: t.naturalWidth, h: t.naturalHeight })
          }}
          onError={() => {
            onError()
          }}
        />
        {trailScreenPoints.length > 1 ? (
          <svg className={style.trailSvg} aria-hidden="true" focusable="false">
            <polyline
              className={style.trailLine}
              points={trailScreenPoints.join(' ')}
              style={{ strokeWidth: `${Math.max(1, (iconSize / 3) * 0.7)}px` }}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ) : null}
        {robotMarker && animatedRobotScreen ? (
          <img
            className={style.robotIcon}
            src={robotMarker.iconUrl}
            alt=""
            draggable={false}
            style={{
              left: `${animatedRobotScreen.left}px`,
              top: `${animatedRobotScreen.top}px`,
              height: `${iconSize}px`,
              width: 'auto',
              transform: `translate(-50%, -50%) rotate(${animatedRobotScreen.rotateDeg}deg)`,
              transition: 'none',
            }}
          />
        ) : null}
      </div>
    </div>
  )
}

/**
 * Full-bleed floor-plan preview over the map panel. Image is centered and limited by the widget (object-fit: contain).
 * Close: dim layer (around image), click outside map widget, Esc, ×.
 */
const GausiumMapPreviewOverlay = ({
  visible,
  previewUrl,
  mapName,
  mapWidgetRootRef,
  onClose,
  insetLeftPx,
  closeOffsetTopPx,
  robotMarker,
  trailPoints,
  loadingPreview,
  noPreviewMessage,
  dismissible = true,
  preserveViewOnSrcChange = false,
}: GausiumMapPreviewOverlayProps) => {
  const { t } = useTranslation()
  const innerRef = useRef<HTMLDivElement>(null)
  const [imageLoadFailed, setImageLoadFailed] = useState(false)

  useEffect(() => {
    setImageLoadFailed(false)
  }, [previewUrl])

  const handleDimClick = useCallback(() => {
    if (!dismissible) return
    onClose()
  }, [dismissible, onClose])

  useEffect(() => {
    if (!visible || !dismissible) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [visible, dismissible, onClose])

  useEffect(() => {
    if (!visible || !dismissible) return
    const onMouseDown = (e: MouseEvent) => {
      const root = mapWidgetRootRef.current
      if (!root || root.contains(e.target as Node)) return
      onClose()
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => document.removeEventListener('mousedown', onMouseDown, true)
  }, [visible, dismissible, onClose, mapWidgetRootRef])

  if (!visible) return null

  const hasImage = !!previewUrl?.trim() && !imageLoadFailed
  const src = hasImage ? resolveMediaSrc(previewUrl!.trim()) : ''
  const titleText = mapName?.trim() || ''

  const layerStyle =
    insetLeftPx != null ? { left: insetLeftPx, top: 0, right: 0, bottom: 0 } : { top: 0, left: 0, right: 0, bottom: 0 }

  return (
    <div className={style.layer} style={layerStyle} data-test-id="gausium-map-preview-overlay">
      <button
        type="button"
        className={style.dim}
        onClick={handleDimClick}
        aria-label="Close map preview"
        style={{ cursor: dismissible ? 'pointer' : 'default' }}
      />
      <div
        ref={innerRef}
        className={style.content}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titleText || 'Map preview'}
      >
        {(titleText || (dismissible && closeOffsetTopPx == null)) && (
          <div className={style.header}>
            {titleText ? <div className={style.title}>{titleText}</div> : <div className={style.title} />}
            {dismissible && closeOffsetTopPx == null && (
              <button type="button" className={style.closeBtn} onClick={onClose} aria-label="Close">
                ×
              </button>
            )}
          </div>
        )}
        {dismissible && closeOffsetTopPx != null && (
          <button
            type="button"
            className={clsx(style.closeBtn, style.closeBtnFloated)}
            style={{ top: closeOffsetTopPx }}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}
        {hasImage ? (
          <LocalFloorMapStage
            src={src}
            alt={titleText || ''}
            marker={robotMarker}
            trailPoints={trailPoints}
            preserveViewOnSrcChange={preserveViewOnSrcChange}
            onError={() => setImageLoadFailed(true)}
          />
        ) : loadingPreview ? (
          <div className={style.noPreview}>
            <div className={style.noPreviewInner}>{t('gausium_map.preview_loading')}</div>
          </div>
        ) : (
          <div className={style.noPreview}>
            <div className={style.noPreviewInner}>{noPreviewMessage || t('gausium_map.preview_unavailable')}</div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GausiumMapPreviewOverlay
